import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Search, RefreshCw, Eye, X, Send } from "lucide-react";

const STATUS_META = {
  queued: { label: "Queued", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
  sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-800 border border-emerald-300" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 border border-red-300" },
};

const EMPTY_FILTERS = { status: "", q: "", template: "", date_from: "", date_to: "" };

export default function AdminEmailLog() {
  const [data, setData] = useState({ items: [], total: 0, counts: {}, templates: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/email-events", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Could not load email log"))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const viewEmail = async (ev) => {
    try {
      const { data } = await api.get(`/admin/email-events/${ev.id}/preview`);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not load preview"); }
  };

  const resend = async (ev) => {
    setResendingId(ev.id);
    try {
      const { data } = await api.post(`/admin/email-events/${ev.id}/resend`);
      if (!data.live) toast.warning(data.detail || "Email is mocked — no live resend");
      else if (data.ok) toast.success("Email resent successfully");
      else toast.error(`Resend failed: ${data.error || "unknown error"}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Resend failed"); }
    finally { setResendingId(null); }
  };

  const retryQueued = async () => {
    try {
      const { data } = await api.post("/admin/email-events/retry-queued");
      if (!data.live) toast.warning(data.detail || "Email is in mock mode");
      else toast.success(`Retried ${data.retried}: ${data.sent} sent, ${data.failed} failed`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Retry failed"); }
  };
  const expireStale = async () => {
    try {
      const { data } = await api.post("/admin/email-events/expire-stale", null, { params: { hours: 24 } });
      toast.success(`${data.expired} stale queued email(s) marked failed`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const c = data.counts || {};

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Mail className="w-4 h-4" /> Communications</div>
        <h1 data-testid="email-log-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Email Log</h1>
        <p className="text-sm text-slate-500 mt-1">Monitor deliverability of all transactional emails. Resend failed emails once Resend is live.</p>

        {/* Status tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {["all", "queued", "sent", "failed"].map((k) => (
            <button key={k} data-testid={`email-count-${k}`} onClick={() => setFilters((f) => ({ ...f, status: k === "all" ? "" : k }))}
              className={`text-left bg-white border p-4 rounded-sm ${(filters.status === k || (k === "all" && !filters.status)) ? "border-[#E63946]" : "border-slate-200"}`}>
              <div className="label-overline text-[9px]">{k}</div>
              <div className="font-data text-3xl mt-1 text-[#0A192F]">{c[k] ?? 0}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">Status</span>
            <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white">
              <option value="">All</option><option value="queued">Queued</option><option value="sent">Sent</option><option value="failed">Failed</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">Recipient</span>
            <div className="flex items-center gap-2 border border-slate-300 rounded-sm px-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input data-testid="filter-recipient" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="email…" className="w-full py-2 text-sm outline-none" />
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">Template</span>
            <select data-testid="filter-template" value={filters.template} onChange={(e) => setFilters({ ...filters, template: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white">
              <option value="">All</option>
              {(data.templates || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">From</span>
            <input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="label-overline text-[10px]">To</span>
            <input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="border border-slate-300 rounded-sm px-3 py-2 text-sm" />
          </label>
          <div className="flex gap-2 lg:col-span-5 flex-wrap">
            <button data-testid="email-log-refresh" onClick={load} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button data-testid="email-retry-queued" onClick={retryQueued} className="inline-flex items-center gap-2 border border-[#E63946] text-[#E63946] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-[#E63946] hover:text-white"><Send className="w-4 h-4" /> Retry Queued</button>
            <button data-testid="email-expire-stale" onClick={expireStale} className="inline-flex items-center gap-2 border border-amber-400 text-amber-700 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-amber-50">Mark Stale Failed (&gt;24h)</button>
            <button data-testid="email-log-clear" onClick={() => setFilters(EMPTY_FILTERS)} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">Clear</button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 label-overline">Recipient</th>
                <th className="text-left p-3 label-overline">Subject</th>
                <th className="text-left p-3 label-overline">Template</th>
                <th className="text-left p-3 label-overline">Status</th>
                <th className="text-left p-3 label-overline">Provider / Msg ID</th>
                <th className="text-left p-3 label-overline">Created</th>
                <th className="text-right p-3 label-overline">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="email-log-table">
              {data.items.map((ev) => {
                const meta = STATUS_META[ev.status] || STATUS_META.queued;
                return (
                  <tr key={ev.id} data-testid={`email-row-${ev.id}`} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="p-3 whitespace-nowrap">{ev.to}<div className="text-[10px] text-slate-400 uppercase">{ev.recipient_role}</div></td>
                    <td className="p-3 max-w-xs"><div className="truncate">{ev.subject || "—"}</div></td>
                    <td className="p-3 font-data text-xs">{(ev.template || "—").replace(/_/g, " ")}</td>
                    <td className="p-3"><span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${meta.cls}`}>{meta.label}</span>
                      {ev.status === "failed" && ev.error && <div className="text-[10px] text-red-500 mt-1 max-w-[180px] truncate" title={ev.error}>{ev.error}</div>}
                    </td>
                    <td className="p-3 text-xs text-slate-500">{ev.provider || "—"}{ev.provider_message_id && <div className="font-data text-[10px] text-slate-400 truncate max-w-[140px]" title={ev.provider_message_id}>{ev.provider_message_id}</div>}</td>
                    <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{ev.created_at ? new Date(ev.created_at).toLocaleString("en-CA") : "—"}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`email-view-${ev.id}`} onClick={() => viewEmail(ev)} title="View email" className="p-2 rounded-sm border border-slate-300 text-[#0A192F] hover:bg-slate-50"><Eye className="w-3.5 h-3.5" /></button>
                        {(ev.status === "failed" || ev.status === "queued") && (
                          <button data-testid={`email-resend-${ev.id}`} onClick={() => resend(ev)} disabled={resendingId === ev.id} title={ev.status === "queued" ? "Send now" : "Resend"} className="p-2 rounded-sm border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white disabled:opacity-50"><Send className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan="7" className="p-12 text-center text-sm text-slate-500"><Mail className="w-6 h-6 mx-auto mb-2 text-slate-300" />No emails match these filters.</td></tr>
              )}
              {loading && <tr><td colSpan="7" className="p-12 text-center label-overline text-slate-400">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">Showing {data.items.length} of {data.total} email(s).</p>
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-preview-modal" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="min-w-0">
                <div className="font-display text-lg uppercase tracking-tight truncate">{preview.subject || "Email Preview"}</div>
                <div className="text-xs text-slate-500 truncate">To: {preview.to}</div>
              </div>
              <button data-testid="email-preview-close" onClick={() => setPreview(null)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-50">
              <iframe title="email-preview" srcDoc={preview.html} className="w-full h-[60vh] border-0" sandbox="" />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
